import { route } from "../../../vendor/tightenco/ziggy";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import moment from "moment";
import axios from "axios";
import { toast } from "vue-sonner";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function useInnerHeight(element) {
  if (!element) {
    return null;
  }

  if (typeof window === "undefined") {
    return null;
  }

  const styles = window.getComputedStyle(element);

  const paddingTop = parseFloat(styles.getPropertyValue("padding-top"));
  const paddingBottom = parseFloat(styles.getPropertyValue("padding-bottom"));

  const paddingY =
    (isNaN(paddingTop) ? 0 : paddingTop) +
    (isNaN(paddingBottom) ? 0 : paddingBottom);

  return element.clientHeight + paddingY;
}

export function useLink({ link }) {
  const { anchor, external_url, text, url, type } = link;

  switch (type) {
    case "pages":
      return {
        href: route("page.builder", { slug: url }),
        title: text,
        target: "_self",
        external: false,
      };
    case "external":
      return {
        href: external_url,
        title: text,
        target: "_blank",
        external: true,
      };
    case "events":
      return {
        href: route("event.details", { slug: url }),
        title: text,
        target: "_self",
        external: false,
      };
    case "news":
    case "research":
      return {
        href: route("page.resource", { slug: url }),
        title: text,
        target: "_self",
        external: false,
      };
    case "anchor":
      return {
        href: `#${anchor}`,
        title: text,
        target: "_self",
        external: false,
      };
  }
}

export function useEventCSS(numberItems = 3) {
  let outer = "";
  switch (numberItems) {
    case 1:
      outer = "";
      break;
    case 2:
      outer = "";
      break;
    case 3:
      outer = "";
      break;
  }
  return {
    outer,
  };
}

export const centsToDollars = (cents) => {
  return (cents / 100).toFixed(2);
};

export const getEventDate = (
  event,
  dateFormat = "dddd DD MMM YY",
  timeFormatTo = "h:mm a",
  timeFormatFrom = "hh:mm:ss",
) => {
  const { start_date, program } = event;
  let time;
  try {
    time = moment(program[0].time, timeFormatFrom).format(timeFormatTo);
  } catch (e) {
    console.error("Error formatting time:", e);
  }

  return `${moment(start_date).format(dateFormat)} ${time ? `| ${time}` : ""}`;
};

export const getResourceDate = (
  date,
  dateFormatTo = "dddd DD MMM YY",
  timeFormatTo = "h:mm a",
  dateFormat = "YYYY-MM-DD",
) => {
  return `${moment(date, dateFormat).format(dateFormatTo)} | ${moment(date, dateFormat).format(timeFormatTo)}`;
};

export const fieldHasError = (errors, field) => {
  return !!(errors && errors[field]);
};

export const getErrorMessage = (errors, field, defaultErrorMessage) => {
  return fieldHasError(errors, field) ? errors[field] : defaultErrorMessage;
};

/**
 * Creates an Axios instance with a CSRF token header.
 * @returns {AxiosInstance}
 */
export const axiosWithToken = () => {
  const instance = axios.create({});
  instance.defaults.headers.common["X-CSRF-TOKEN"] = document
    .querySelector('meta[name="csrf-token"]')
    .getAttribute("content");

  return instance;
};

/**
 * Shows a toast notification for form errors.
 * @param error
 * @param message
 */
export const showFormError = (error, message = null) => {
  console.error("Form error:", error);
  if (error) {
    toast.error("Oops", {
      duration: 4500,
      description: message ?? "Please check the form for errors",
    });
  }
};

/**
 * Checks if a string contains another string (case-sensitive).
 * Mimics PHP's str_contains() function using JavaScript's String.prototype.includes().
 *
 * @param {string} haystack The string to search within.
 * @param {string} needle   The string to search for.
 * @returns {boolean} True if haystack contains needle, false otherwise.
 * @throws {TypeError} If haystack or needle is not provided or not coercible to a string
 * in a way that .includes() can handle (though .includes() itself
 * coerces arguments to strings). A stricter check is added for clarity.
 */
export function str_contains(haystack, needle) {
  if (
    haystack === null ||
    haystack === undefined ||
    needle === null ||
    needle === undefined
  ) {
    throw new TypeError("str_contains() expects string arguments.");
  }

  // Convert arguments to strings to ensure .includes method exists
  const haystackStr = String(haystack);
  const needleStr = String(needle);

  // Use the built-in String.prototype.includes() method
  return haystackStr.includes(needleStr);
}

export const nl2br = (str, is_xhtml) => {
  const breakTag =
    is_xhtml || typeof is_xhtml === "undefined" ? "<br " + "/>" : "<br>";

  return (str + "").replace(
    /([^>\r\n]?)(\r\n|\n\r|\r|\n)/g,
    "$1" + breakTag + "$2",
  );
};
